// src/components/Profile.js
import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../components/AuthContext';
import authService from '../services/authService';
import userService from '../services/userService';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import SEO from './SEO';
import UserTournamentRanks from './UserTournamentRanks';
import '../styles/Profile.css';
import { FiUser, FiEdit2, FiSave, FiX, FiBell, FiShield, FiLogOut, FiAward } from 'react-icons/fi';

const Profile = () => {
  const { userName, userId, login, forceLogout } = useContext(AuthContext);
  const [newUsername, setNewUsername] = useState(userName || '');
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loggingOutAll, setLoggingOutAll] = useState(false);

  // Nastavení e-mailových notifikací
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [leadHours, setLeadHours] = useState(12);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  const { t } = useTranslation();

  // Synchronizace stavu, pokud se načte userName později
  useEffect(() => {
    if (userName) setNewUsername(userName);
  }, [userName]);

  // Načtení nastavení notifikací
  useEffect(() => {
    let active = true;
    const fetchSettings = async () => {
      try {
        const data = await userService.getNotificationSettings();
        if (!active) return;
        setNotificationsEnabled(Boolean(data?.enabled));
        if (data?.leadHours != null) setLeadHours(data.leadHours);
      } catch (error) {
        console.warn('Failed to load notification settings', error);
      } finally {
        if (active) setLoadingSettings(false);
      }
    };
    fetchSettings();
    return () => { active = false; };
  }, []);

  const handleSaveNotificationSettings = async () => {
    const hours = Number(leadHours);
    if (!Number.isInteger(hours) || hours < 1 || hours > 168) {
      toast.error(t('notificationsLeadHoursInvalid', { defaultValue: 'Čas upozornění musí být mezi 1 a 168 hodinami' }));
      return;
    }
    setSavingSettings(true);
    try {
      const data = await userService.updateNotificationSettings({ enabled: notificationsEnabled, leadHours: hours });
      setNotificationsEnabled(Boolean(data?.enabled));
      if (data?.leadHours != null) setLeadHours(data.leadHours);
      toast.success(t('notificationsSaved', { defaultValue: 'Nastavení notifikací uloženo' }));
    } catch (error) {
      const detail = error?.response?.data || error?.message || '';
      toast.error(t('notificationsSaveError', { defaultValue: 'Nepodařilo se uložit nastavení notifikací' }) + (detail ? `: ${detail}` : ''));
    } finally {
      setSavingSettings(false);
    }
  };

  const handleChangeUsername = async () => {
    // Validation
    if (!newUsername || newUsername.trim() === '') {
      toast.error(t('usernameRequired') || 'Username is required');
      return;
    }

    if (newUsername.trim() === userName) {
      toast.info(t('usernameUnchanged') || 'Username is the same as before');
      setIsEditing(false);
      return;
    }

    if (newUsername.trim().length < 3) {
      toast.error(t('usernameTooShort') || 'Username must be at least 3 characters');
      return;
    }

    setIsLoading(true);

    try {
      await authService.updateUsername(newUsername.trim());
      await login();
      toast.success(t('usernameChanged') || 'Username changed successfully!');
      setIsEditing(false);
    } catch (error) {
      let errorMessage = t('usernameChangeError') || 'Failed to change username';

      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        switch (status) {
          case 400:
            errorMessage = typeof data === 'string' ? data : (t('usernameInvalid') || 'Invalid username');
            break;
          case 401:
            errorMessage = t('sessionExpired') || 'Session expired';
            break;
          case 409:
            errorMessage = t('usernameTaken') || 'Username taken';
            break;
          default:
            errorMessage = typeof data === 'string' ? data : errorMessage;
        }
      }
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setNewUsername(userName);
    setIsEditing(false);
  };

  const handleLogoutAllDevices = async () => {
    const confirmed = window.confirm(
      t('logoutAllConfirm', {
        defaultValue: 'Opravdu se chceš odhlásit ze všech zařízení? Budeš se muset všude znovu přihlásit.',
      })
    );
    if (!confirmed) return;

    setLoggingOutAll(true);
    try {
      await authService.logoutAllDevices();
      toast.success(
        t('logoutAllSuccess', { defaultValue: 'Byl(a) jsi odhlášen(a) ze všech zařízení.' })
      );
      // Zneplatnili jsme i aktuální session → odhlásíme lokálně a přesměrujeme na login.
      forceLogout();
    } catch (error) {
      toast.error(
        t('logoutAllError', { defaultValue: 'Nepodařilo se odhlásit ze všech zařízení.' })
      );
      setLoggingOutAll(false);
    }
  };

  return (
    <div className="profile-container">
      <SEO title={t('profileTitle', { defaultValue: 'Profil' })} />

      {/* Hlavička s avatarem */}
      <div className="profile-header-card">
        <div className="profile-avatar">
          {userName ? userName.charAt(0).toUpperCase() : '?'}
        </div>
        <div className="profile-header-info">
          <h1>{userName}</h1>
          <p>{t('welcomeBack', { defaultValue: 'Scoreo Profil' })}</p>
        </div>
      </div>

      <div className="profile-grid">
        {/* Karta: Nastavení účtu */}
        <div className="profile-card">
          <div className="profile-card-header">
            <span className="profile-card-icon"><FiUser /></span>
            <h3>{t('accountSettings', { defaultValue: 'Nastavení účtu' })}</h3>
          </div>
          <div className="profile-card-body">
            <div className="profile-field">
              <label>{t('usernameSimple', { defaultValue: 'Uživatelské jméno' })}</label>
              {isEditing ? (
                <input
                  className="profile-input"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  disabled={isLoading}
                  autoFocus
                />
              ) : (
                <div className="profile-username-row">
                  <span className="profile-username-value">{userName}</span>
                  <button
                    type="button"
                    className="profile-icon-btn"
                    onClick={() => setIsEditing(true)}
                    aria-label={t('edit', { defaultValue: 'Upravit' })}
                  >
                    <FiEdit2 />
                  </button>
                </div>
              )}
            </div>
          </div>
          {isEditing && (
            <div className="profile-btn-row">
              <button
                className="profile-btn is-full"
                onClick={handleChangeUsername}
                disabled={isLoading}
              >
                {isLoading ? <span className="profile-spinner" /> : <FiSave />}
                {t('save')}
              </button>
              <button
                className="profile-btn is-secondary is-full"
                onClick={handleCancel}
                disabled={isLoading}
              >
                <FiX />
                {t('cancel', { defaultValue: 'Zrušit' })}
              </button>
            </div>
          )}
        </div>

        {/* Karta: Notifikace */}
        <div className="profile-card">
          <div className="profile-card-header">
            <span className="profile-card-icon"><FiBell /></span>
            <h3>{t('notificationsSettings', { defaultValue: 'Notifikace' })}</h3>
          </div>

          {loadingSettings ? (
            <div className="profile-loading">
              <span className="profile-spinner is-dark" />
            </div>
          ) : (
            <>
              <p className="profile-card-desc">
                {t('notificationsDesc', { defaultValue: 'Dostávej e-mailem upozornění na zápasy ve svých ligách, které ještě nemáš natipované a brzy začínají.' })}
              </p>
              <div className="profile-card-body">
                <label className="profile-toggle">
                  <input
                    type="checkbox"
                    checked={notificationsEnabled}
                    onChange={(e) => setNotificationsEnabled(e.target.checked)}
                    disabled={savingSettings}
                  />
                  <span className="profile-toggle-track" />
                  <span className="profile-toggle-label">
                    {t('notificationsEnabledLabel', { defaultValue: 'Posílat e-mailová upozornění' })}
                  </span>
                </label>
                <div className="profile-field">
                  <label>{t('notificationsLeadHoursLabel', { defaultValue: 'Upozornit X hodin před začátkem' })}</label>
                  <input
                    className="profile-input is-narrow"
                    type="number"
                    min={1}
                    max={168}
                    value={leadHours}
                    onChange={(e) => setLeadHours(e.target.value)}
                    disabled={savingSettings || !notificationsEnabled}
                  />
                  <p className="profile-field-hint">
                    {t('notificationsLeadHoursHelp', { defaultValue: '1 až 168 hodin (max 7 dní)' })}
                  </p>
                </div>
              </div>
              <div className="profile-btn-row">
                <button
                  className="profile-btn"
                  onClick={handleSaveNotificationSettings}
                  disabled={savingSettings}
                >
                  {savingSettings ? <span className="profile-spinner" /> : <FiSave />}
                  {t('save')}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Karta: Moje turnaje */}
        <div className="profile-card">
          <div className="profile-card-header">
            <span className="profile-card-icon"><FiAward /></span>
            <h3>{t('tournamentRanks.title', { defaultValue: 'Moje turnaje' })}</h3>
          </div>
          <p className="profile-card-desc">
            {t('tournamentRanks.desc', { defaultValue: 'Turnaje, kterých ses účastnil(a), a tvoje umístění v globálním žebříčku.' })}
          </p>
          <div className="profile-card-body">
            <UserTournamentRanks userId={userId} />
          </div>
        </div>

        {/* Karta: Zabezpečení */}
        <div className="profile-card">
          <div className="profile-card-header">
            <span className="profile-card-icon"><FiShield /></span>
            <h3>{t('securitySettings', { defaultValue: 'Zabezpečení' })}</h3>
          </div>
          <p className="profile-card-desc">
            {t('logoutAllDesc', {
              defaultValue: 'Odhlásí tě ze všech zařízení a prohlížečů, kde jsi přihlášen(a). Použij, pokud máš podezření, že k tvému účtu má přístup někdo další.',
            })}
          </p>
          <div className="profile-btn-row">
            <button
              className="profile-btn is-secondary is-full"
              onClick={handleLogoutAllDevices}
              disabled={loggingOutAll}
            >
              {loggingOutAll ? <span className="profile-spinner is-dark" /> : <FiLogOut />}
              {t('logoutAllButton', { defaultValue: 'Odhlásit ze všech zařízení' })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
